import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import { Refresh, Download } from '@mui/icons-material';
import ActionButton from './ActionButton.jsx';
import ShareControl from './ShareControl.jsx';
import SaveMapButton from './SaveMapButton.jsx';
import QuickOrderStrip from './QuickOrderStrip.jsx';
import { PALETTE } from '../theme';

/**
 * Post-map action section. Hero treatment is the QuickOrderStrip — three
 * featured products with Order buttons — because that's what we're actually
 * trying to sell. Reset/Download/Share are kept as quieter secondary actions
 * underneath so people who want the free download still have one click.
 *
 * Hidden until the user has marked at least one place; before then we show
 * a tiny inline hint.
 */
const MapActions = ({
    visitedCount,
    loading,
    onReset,
    onDownload,
    share,
    save,
    prepareImage,
    onOrder,
    defaultLabel,
    designSrc,
}) => {
    const hasSelection = visitedCount > 0;

    if (!hasSelection) {
        return (
            <Typography
                variant="caption"
                sx={{
                    display: 'block',
                    textAlign: 'center',
                    color: PALETTE.textMuted,
                    mt: { xs: 1, sm: 1.25 },
                    mb: { xs: 2, sm: 2 }
                }}
            >
                👆 Tap places on the map or use the checklist below to download or order.
            </Typography>
        );
    }

    return (
        <Box sx={{ mt: { xs: 1, sm: 1.5 } }}>
            {prepareImage && onOrder && (
                <QuickOrderStrip
                    defaultLabel={defaultLabel}
                    prepareImage={prepareImage}
                    onOrder={onOrder}
                    designSrc={designSrc}
                />
            )}

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'row', sm: 'row' },
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexWrap: 'wrap',
                    gap: { xs: 0.75, sm: 1 },
                    mb: { xs: 2.5, sm: 3 }
                }}
            >
                <ActionButton
                    variant="ghost"
                    startIcon={<Download sx={{ fontSize: 18 }} />}
                    onClick={onDownload}
                    disabled={loading}
                    sx={{ width: 'auto', minWidth: 0, px: 1.5 }}
                >
                    Download
                </ActionButton>
                {save && (
                    <SaveMapButton
                        getSnapshot={save.getSnapshot}
                        openMap={save.openMap}
                        onSaved={save.onSaved}
                        onStatus={save.onStatus}
                        variant="ghost"
                        sx={{ width: 'auto', minWidth: 0, px: 1.5 }}
                    />
                )}
                <ShareControl
                    kind="travel"
                    region={share.region}
                    visited={share.visited}
                    total={share.total}
                    label={share.label}
                    getBlob={share.getBlob}
                    fileName={share.fileName}
                    trackId={share.trackId}
                    variant="ghost"
                    sx={{ width: 'auto', minWidth: 0, px: 1.5 }}
                />
                <ActionButton
                    variant="ghost"
                    startIcon={<Refresh sx={{ fontSize: 18 }} />}
                    onClick={onReset}
                    sx={{ width: 'auto', minWidth: 0, px: 1.5 }}
                >
                    Reset
                </ActionButton>
            </Box>
        </Box>
    );
};

MapActions.propTypes = {
    visitedCount: PropTypes.number.isRequired,
    loading: PropTypes.bool,
    onReset: PropTypes.func.isRequired,
    onDownload: PropTypes.func.isRequired,
    share: PropTypes.shape({
        region: PropTypes.string,
        visited: PropTypes.number,
        total: PropTypes.number,
        label: PropTypes.string,
        getBlob: PropTypes.func,
        fileName: PropTypes.string,
        trackId: PropTypes.string
    }).isRequired,
    save: PropTypes.shape({
        getSnapshot: PropTypes.func,
        openMap: PropTypes.object,
        onSaved: PropTypes.func,
        onStatus: PropTypes.func
    }),
    prepareImage: PropTypes.func,
    onOrder: PropTypes.func,
    defaultLabel: PropTypes.string,
    designSrc: PropTypes.string,
};

export default MapActions;
